import React, { useState } from 'react';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../context/AuthContext';
import { MAPBOX_TOKEN } from '../firebaseConfig';
import Swal from 'sweetalert2';
import './SignupModal.css';

const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app);

const SignupModal = ({ onClose, onLoginClick }) => {
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'reset'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [consents, setConsents] = useState({
    smsEmail: false,
    privacyPolicy: false,
    termsOfService: false,
  });

  const searchAddress = async (query) => {
    if (!query || query.length < 3) return;
    if (!MAPBOX_TOKEN) {
      console.error('[SignupModal] Mapbox token not configured');
      return;
    }
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&types=address,place`
      );
      const data = await response.json();
      setAddressSuggestions(data.features || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Address search error:', error);
    }
  };

  const selectAddress = (place) => {
    setAddressQuery(place.place_name);
    setSelectedAddress(place);
    setShowSuggestions(false);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSignup = async () => {
    if (!formData.name.trim() || !formData.email.trim() || !formData.password) {
      Swal.fire('Error', 'Please fill in all required fields.', 'error');
      return;
    }
    if (formData.password.length < 6) {
      Swal.fire('Error', 'Password must be at least 6 characters.', 'error');
      return;
    }
    if (!selectedAddress) {
      Swal.fire('Error', 'Please enter and select your address.', 'error');
      return;
    }
    if (!consents.smsEmail || !consents.privacyPolicy || !consents.termsOfService) {
      Swal.fire('Error', 'Please accept all required consents to continue.', 'error');
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email.trim(), formData.password);
      const user = userCredential.user;

      await updateProfile(user, { displayName: formData.name.trim() });

      const [lng, lat] = selectedAddress.center || [0, 0];
      // Every FodyFee account (consumer or business) lives at users/{uid}
      // now — this used to write to a separate `consumers/{uid}` collection,
      // which meant a consumer who later onboarded a restaurant (Stripe
      // onboarding, business dashboard) ended up with two disconnected
      // accounts under the same uid. `merge: true` so this never clobbers
      // an existing business profile if this uid already has one.
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: selectedAddress.place_name,
        lat,
        lng,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        consents: {
          smsEmailConsent: consents.smsEmail,
          privacyPolicy: consents.privacyPolicy,
          termsOfService: consents.termsOfService,
          consentedAt: serverTimestamp(),
        },
      }, { merge: true });

      // Create Stripe customer on the platform account
      try {
        const createStripeCustomer = httpsCallable(functions, 'createPlatformStripeCustomer');
        await createStripeCustomer({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
        });
      } catch (stripeErr) {
        console.error('Error creating Stripe customer on signup:', stripeErr);
        // Non-blocking: don't fail signup if Stripe customer creation fails
      }

      Swal.fire({
        title: 'Welcome!',
        text: 'Your account has been created successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
      onClose();
    } catch (error) {
      console.error('Signup error:', error);
      Swal.fire('Error', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!formData.email.trim() || !formData.password) {
      Swal.fire('Error', 'Please enter email and password.', 'error');
      return;
    }
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, formData.email.trim(), formData.password);
      Swal.fire({
        title: 'Welcome back!',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
      onClose();
    } catch (error) {
      console.error('Login error:', error);
      Swal.fire('Error', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!formData.email.trim()) {
      Swal.fire('Error', 'Please enter your email address.', 'error');
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, formData.email.trim());
      setResetSent(true);
      Swal.fire({
        title: 'Email Sent!',
        text: 'Check your inbox for the password reset link.',
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Reset password error:', error);
      Swal.fire('Error', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Google sign-in (web popup flow — no extra packages needed). Works for
  // BOTH login and signup: first-time Google users get their profile created
  // at users/{uid} (same unified home as email signups), existing users just
  // sign in. The consumers/ → users/ self-healing migration in AuthContext
  // still runs on load for legacy accounts.
  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      const result = await signInWithPopup(auth, provider);
      const gUser = result.user;

      // First-time Google user → create the unified profile at users/{uid}
      const userRef = doc(db, 'users', gUser.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          uid: gUser.uid,
          name: gUser.displayName || '',
          email: gUser.email || '',
          phone: gUser.phoneNumber || '',
          photoUrl: gUser.photoURL || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          consents: {
            smsEmailConsent: true, // implied by using the service (see signup consent text)
            privacyPolicy: true,
            termsOfService: true,
            consentedAt: serverTimestamp(),
            via: 'google',
          },
        }, { merge: true });

        // Create Stripe customer on the platform account (non-blocking)
        try {
          const createStripeCustomer = httpsCallable(functions, 'createPlatformStripeCustomer');
          await createStripeCustomer({
            name: gUser.displayName || '',
            email: gUser.email || '',
            phone: gUser.phoneNumber || '',
          });
        } catch (stripeErr) {
          console.error('Error creating Stripe customer on Google signup:', stripeErr);
        }
      }

      Swal.fire({
        title: 'Welcome!',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
      onClose();
    } catch (error) {
      // User closing the popup is not an error worth shouting about
      if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        console.error('Google sign-in error:', error);
        Swal.fire('Error', error.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ff-modal-overlay" onClick={onClose}>
      <div className="ff-auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ff-auth-header">
          <h3>
            {mode === 'reset'
              ? 'Reset Password'
              : mode === 'login'
                ? 'Welcome Back'
                : 'Join FodyFee'}
          </h3>
          <p className="ff-auth-subtitle">
            {mode === 'reset'
              ? 'Enter your email and we will send you a reset link.'
              : mode === 'login'
                ? 'Log in to order from your favorite local spots.'
                : 'Create an account to discover and order from local restaurants.'}
          </p>
          <button className="ff-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Toggle */}
        {mode !== 'reset' && (
          <div className="ff-auth-toggle">
            <button
              className={mode === 'login' ? 'active' : ''}
              onClick={() => setMode('login')}
            >
              Log In
            </button>
            <button
              className={mode === 'signup' ? 'active' : ''}
              onClick={() => setMode('signup')}
            >
              Sign Up
            </button>
          </div>
        )}

        <div className="ff-auth-body">
          {mode !== 'reset' && (
            <>
              <button
                type="button"
                className="ff-btn ff-btn-block"
                style={{
                  background: '#fff',
                  color: '#1f2937',
                  border: '1px solid #dadce0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  marginBottom: 16,
                }}
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.7-.4-3.9z"/>
                </svg>
                Continue with Google
              </button>
              <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 16px', gap: 10 }}>
                <div style={{ flex: 1, height: 1, background: '#e5e7eb' }}></div>
                <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>or with email</span>
                <div style={{ flex: 1, height: 1, background: '#e5e7eb' }}></div>
              </div>
            </>
          )}

          {mode === 'signup' && (
            <div className="ff-auth-field">
              <label>Full Name <span className="ff-required">*</span></label>
              <div className="ff-input-wrap">
                <i className="fas fa-user ff-input-icon"></i>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Doe"
                />
              </div>
            </div>
          )}

          <div className="ff-auth-field">
            <label>Email <span className="ff-required">*</span></label>
            <div className="ff-input-wrap">
              <i className="fas fa-envelope ff-input-icon"></i>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="you@example.com"
                autoFocus={mode === 'reset'}
              />
            </div>
            {mode === 'reset' && resetSent && (
              <div className="alert alert-success mt-2 py-2" style={{ fontSize: '0.85rem' }}>
                <i className="fas fa-check-circle mr-2"></i>
                Reset link sent! Check your inbox.
              </div>
            )}
          </div>

          {mode === 'signup' && (
            <div className="ff-auth-field">
              <label>Phone</label>
              <div className="ff-input-wrap">
                <i className="fas fa-phone ff-input-icon"></i>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          )}

          {mode === 'signup' && (
            <div className="ff-auth-field" style={{ position: 'relative' }}>
              <label>Address <span className="ff-required">*</span></label>
              {!MAPBOX_TOKEN && (
                <div className="alert alert-warning mb-2 py-2" style={{ fontSize: '0.8rem' }}>
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  Mapbox token missing.
                </div>
              )}
              <div className="ff-input-wrap">
                <i className="fas fa-map-marker-alt ff-input-icon"></i>
                <input
                  type="text"
                  value={addressQuery}
                  onChange={(e) => {
                    setAddressQuery(e.target.value);
                    searchAddress(e.target.value);
                  }}
                  placeholder="Search your address..."
                />
              </div>
              {showSuggestions && addressSuggestions.length > 0 && (
                <div className="ff-suggestions">
                  {addressSuggestions.map((place, i) => (
                    <div key={i} className="ff-suggestion-item" onClick={() => selectAddress(place)}>
                      <i className="fas fa-map-marker-alt"></i>
                      {place.place_name}
                    </div>
                  ))}
                </div>
              )}
              {selectedAddress && (
                <small className="text-success d-block mt-1"><i className="fas fa-check mr-1"></i>Address selected</small>
              )}
            </div>
          )}

          {mode !== 'reset' && (
            <>
              <div className="ff-auth-field">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label>Password {mode !== 'reset' && <span className="ff-required">*</span>}</label>
                  {mode === 'login' && (
                    <button
                      type="button"
                      className="ff-link-sm"
                      onClick={() => { setMode('reset'); setResetSent(false); }}
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="ff-input-wrap">
                  <i className="fas fa-lock ff-input-icon"></i>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder={mode === 'signup' ? 'Min 6 characters' : 'Your password'}
                  />
                </div>
              </div>

              {mode === 'signup' && (
                <div className="ff-auth-field">
                  <label>Confirm Password <span className="ff-required">*</span></label>
                  <div className="ff-input-wrap">
                    <i className="fas fa-lock ff-input-icon"></i>
                    <input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e.target.value)}
                      placeholder="Repeat password"
                    />
                  </div>
                </div>
              )}

              {mode === 'signup' && (
                <div className="ff-auth-field">
                  <label style={{ textTransform: 'none', letterSpacing: 'normal', fontWeight: 600 }}>
                    Required Consents <span className="ff-required">*</span>
                  </label>
                  <div className="ff-consent-list">
                    <label className="ff-consent-item">
                      <input
                        type="checkbox"
                        checked={consents.smsEmail}
                        onChange={(e) => setConsents(prev => ({ ...prev, smsEmail: e.target.checked }))}
                      />
                      <span>
                        (Optional) SMS and Email Consent — By providing your phone number and email and checking this box, you agree to receive SMS notifications from FodyFee regarding order status, account security, service updates, and promotional/marketing offers. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance.
                      </span>
                    </label>
                    <label className="ff-consent-item">
                      <input
                        type="checkbox"
                        checked={consents.privacyPolicy}
                        onChange={(e) => setConsents(prev => ({ ...prev, privacyPolicy: e.target.checked }))}
                      />
                      <span>
                        I agree to the{' '}
                        <a href="https://fodyfee.com/privacy" target="_blank" rel="noopener noreferrer" className="ff-link-sm">Privacy Policy</a>
                      </span>
                    </label>
                    <label className="ff-consent-item">
                      <input
                        type="checkbox"
                        checked={consents.termsOfService}
                        onChange={(e) => setConsents(prev => ({ ...prev, termsOfService: e.target.checked }))}
                      />
                      <span>
                        I agree to the{' '}
                        <a href="https://fodyfee.com/terms" target="_blank" rel="noopener noreferrer" className="ff-link-sm">Terms of Service</a>
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="ff-auth-footer">
          <button
            className="ff-btn ff-btn-primary ff-btn-block"
            onClick={mode === 'login' ? handleLogin : mode === 'reset' ? handleResetPassword : handleSignup}
            disabled={loading || (mode === 'signup' && (!consents.privacyPolicy || !consents.termsOfService))}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : mode === 'login' ? (
              <><i className="fas fa-sign-in-alt mr-2"></i>Log In</>
            ) : mode === 'reset' ? (
              <><i className="fas fa-paper-plane mr-2"></i>Send Reset Link</>
            ) : (
              <><i className="fas fa-user-plus mr-2"></i>Create Account</>
            )}
          </button>
          {mode === 'reset' && (
            <button
              className="ff-btn ff-btn-ghost ff-btn-block mt-2"
              onClick={() => { setMode('login'); setResetSent(false); }}
            >
              <i className="fas fa-arrow-left mr-2"></i>Back to Log In
            </button>
          )}
          {mode !== 'reset' && (
            <button className="ff-btn ff-btn-ghost ff-btn-block mt-2" onClick={onClose}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupModal;
